import React, { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import SearchSummaryList from './SearchSummaryList'
import SearchTabs from './SearchTabs'
import SearchDetailList from './SearchDetailList'
import { getSearchSummary, getSearchDetail } from '@/api/main'

const TAB_MAP = {
  all: { title: '전체', countKey: 'allCount', previewKey: null },
  course: { title: '강좌', countKey: 'courseCount', previewKey: 'coursePreview' },
  xr: { title: 'XR콘텐츠', countKey: 'xrCount', previewKey: 'xrPreview' },
  notice: { title: '공지사항', countKey: 'noticeCount', previewKey: 'noticePreview' },
  faq: { title: 'FAQ', countKey: 'faqCount', previewKey: 'faqPreview' },
}

export default function SearchPage() {
  const { search } = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(search)

  const keyword = params.get('keyword') || ''
  const currentTab = params.get('tab') || 'all'

  const [summary, setSummary] = useState(null)

  const [detailList, setDetailList] = useState([])
  const [detailTotal, setDetailTotal] = useState(0)

  const [loadingDetail, setLoadingDetail] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const [page, setPage] = useState(1)
  const pageSize = 10

  const currentMeta = TAB_MAP[currentTab] ?? {}

  const tabs = useMemo(() => {
    return Object.keys(TAB_MAP).map((key) => ({
      key,
      label: TAB_MAP[key].title,
      count: summary ? summary[TAB_MAP[key].countKey] : 0,
    }))
  }, [summary])

  // summary: keyword 있을 때만 호출
  useEffect(() => {
    let alive = true

    async function run() {
      try {
        const res = await getSearchSummary({ keyword })
        if (!alive) return
        setSummary(res)
      } catch (e) {
        console.error(e)
        if (!alive) return
        setSummary(null)
      }
    }

    if (keyword.trim()) run()
    else setSummary(null)

    return () => { alive = false }
  }, [keyword])

  // keyword/tab 바뀌면 detail 초기화
  useEffect(() => {
    setDetailList([])
    setDetailTotal(0)
    setPage(1)
  }, [keyword, currentTab])

  // detail: all 이 아닐 때만, page 기반으로 요청
  useEffect(() => {
    let alive = true

    async function run() {
      if (!keyword.trim()) return
      if (currentTab === 'all') return

      const isFirst = page === 1

      try {
        if (isFirst) setLoadingDetail(true)
        else setLoadingMore(true)

        const res = await getSearchDetail({
          keyword,
          tab: currentTab,
          page,
          pageSize,
        })

        if (!alive) return

        const items = Array.isArray(res?.items) ? res.items : []
        const total = Number(res?.total || 0)

        setDetailTotal(total)
        setDetailList((prev) => (isFirst ? items : prev.concat(items)))
      } catch (e) {
        console.error(e)
        if (!alive) return
        if (page === 1) {
          setDetailList([])
          setDetailTotal(0)
        }
      } finally {
        if (!alive) return
        if (page === 1) setLoadingDetail(false)
        else setLoadingMore(false)
      }
    }

    run()
    return () => { alive = false }
  }, [keyword, currentTab, page])

  const onTabChange = (tab) => {
    navigate(`/search?keyword=${encodeURIComponent(keyword)}&tab=${tab}`)
  }

  const hasMore =
    currentTab !== 'all' &&
    detailList.length < detailTotal

  const handleLoadMore = () => {
    if (loadingMore) return
    if (!hasMore) return
    setPage((p) => p + 1)
  }

  return (
    <Box>
      <SearchTabs currentTab={currentTab} onChange={onTabChange} tabs={tabs} />

      {currentTab === 'all' ? (
        <SearchSummaryList summary={summary} keyword={keyword} TAB_MAP={TAB_MAP} />
      ) : (
        <SearchDetailList
          loading={loadingDetail}
          loadingMore={loadingMore}
          title={currentMeta.title}
          count={summary ? summary[currentMeta.countKey] : 0}
          items={detailList}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
        />
      )}
    </Box>
  )
}
